import { QueryKey, useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  GetUserPreferencesV2CommandInput,
  GetUserPreferencesV2CommandOutput,
  ProfileSettings,
} from '@amzn/gbsa-orbit-typescript-client';
import { UserNotFoundException } from '@amzn/gbsa-orbit-typescript-client';
import { userPreferencesService } from 'src/data/api/orbit-service-ts/user-preferences/userPreferencesService';
import { userPreferencesKeys } from 'src/data/api/orbit-service-ts/user-preferences/queries/keys';

type UserPreferencesData = GetUserPreferencesV2CommandOutput['preferences'] & {
  profileSettings: ProfileSettings & {
    businessUnitLocationPreferences: string[];
  };
};
export const useUserPreferencesQuery = <TData = UserPreferencesData>(
  { programId, userId: userAlias }: Partial<GetUserPreferencesV2CommandInput>,
  options?: Omit<
    UseQueryOptions<UserPreferencesData, Error, TData, QueryKey>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    ...options,
    queryKey: userPreferencesKeys.get({ programId, userId: userAlias }),
    enabled: !!userAlias && !!programId,
    queryFn: async () => {
      if (!userAlias || !programId) {
        throw new Error('Missing required parameters');
      }
      try {
        const resp = await userPreferencesService.get({
          userId: userAlias,
          programId: programId,
        });
        const buLocations =
          resp.preferences.profileSettings?.businessUnitPreferences?.find(
            (item) =>
              item.businessUnit === resp.preferences.profileSettings?.businessUnit,
          )?.locations;
        return {
          ...resp.preferences,
          profileSettings: {
            ...resp.preferences.profileSettings,
            businessUnitLocationPreferences: buLocations ?? [],
          },
        };
      } catch (error) {
        if (error instanceof UserNotFoundException) {
          return {
            createdAt: new Date(),
            lastUpdatedAt: new Date(),
            lastUpdatedBy: userAlias,
            programId,
            userId: userAlias,
            profileSettings: {
              businessUnit: 'AMZL',
              businessUnitLocationPreferences: [],
            },
            notificationSettings: {},
          };
        }
        throw error;
      }
    },
  });
